import { format } from 'date-fns'

import { FtueVideo } from '../../../../api/core'
import { Market } from '../../../../features/markets/types/Market'
import { chaptersMatch, toChapterBodies, translationsMatch } from '../../../components/FtueChapterEditor/ftueChapterHelpers'
import { thumbnailsMatch, toVimeoThumbnails, VimeoOEmbed } from '../../../helpers/vimeo'
import { FTUE_VIDEO_TYPE, FtueGameVersion, FtueMarketOption, FtueVideoAttachBody, FtueVideoFormValues, FtueVideoUpdateBody } from '../types/InternalFtueVideo'

/** Newest first, as customers see a game's videos. */
export const sortFtueVideos = (videos: FtueVideo[]) => [...videos].sort((a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id))

export const filterFtueVideos = (videos: FtueVideo[], term: string) => {
  const needle = term.trim().toLowerCase()
  if (!needle) return videos
  return videos.filter((video) =>
    [video.names?.en, video.gameVersion, video.marketIso, video.vimeoId, video.gameId].some((value) => value?.toLowerCase().includes(needle))
  )
}

export const findVideoWithVimeoId = (videos: FtueVideo[] | undefined, vimeoId: string) => videos?.find((video) => video.vimeoId === vimeoId)

/** Customers' FTUE cards show the 295×166 picture. */
export const getFtueCardThumbnail = (video: Pick<FtueVideo, 'thumbnails'>) => video.thumbnails?.[2] ?? video.thumbnails?.[0]

export const getFtueRowThumbnail = (video: Pick<FtueVideo, 'thumbnails'>) => video.thumbnails?.[1] ?? video.thumbnails?.[0]

/** Customers only list videos of type `ftue`; an attached video gets it when it is first saved. */
export const isVisibleToCustomers = (video: Pick<FtueVideo, 'type'>) => video.type === FTUE_VIDEO_TYPE

export const formatReleaseDate = (timestamp?: number) => (timestamp ? format(new Date(timestamp), 'yyyy-MM-dd') : '---')

/** The versions answer has one entry per crawl; keep the newest entry of each version. */
export const toFtueGameVersions = (versions: FtueGameVersion[]): FtueGameVersion[] => {
  const seen = new Set<string>()
  return [...versions]
    .sort((a, b) => b.versionReleaseDate - a.versionReleaseDate)
    .filter(({ version }) => (seen.has(version) ? false : (seen.add(version), true)))
    .map(({ version, versionReleaseDate }) => ({ version, versionReleaseDate }))
}

/** Keeps a stored version the market no longer lists, so an unchanged save sends it back. */
export const withStoredVersion = (versions: FtueGameVersion[], stored?: FtueGameVersion) =>
  !stored?.version || versions.some(({ version }) => version === stored.version) ? versions : [stored, ...versions]

/** The game's markets, ISO codes as stored (a few are upper case), plus a stored market the game doesn't list. */
export const getFtueMarketOptions = (markets: Pick<Market, 'iso' | 'name'>[] | undefined, storedIso?: string): FtueMarketOption[] => {
  const options = (markets ?? []).map(({ iso, name }) => ({ iso, name: name || iso }))
  return storedIso && !options.some(({ iso }) => iso === storedIso) ? [{ iso: storedIso, name: storedIso }, ...options] : options
}

export const getDefaultMarketIso = (options: FtueMarketOption[]) => options.find(({ iso }) => iso === 'us')?.iso ?? options[0]?.iso ?? ''

export const toFtueVideoAttachBody = ({
  gameId,
  oembed,
  name,
  marketIso,
  version,
}: {
  gameId: string
  oembed: VimeoOEmbed
  name: string
  marketIso: string
  version: FtueGameVersion
}): FtueVideoAttachBody => ({
  gameId,
  gameVersion: version.version,
  vimeoId: oembed.vimeoId,
  marketIso,
  names: { en: name.trim() },
  gameVersionReleaseDate: version.versionReleaseDate,
  thumbnails: toVimeoThumbnails(oembed.thumbnailUrl, { keepQuery: true }),
  chapters: [],
})

export const toFtueVideoFormValues = (video?: FtueVideo | null): FtueVideoFormValues => ({
  names: video?.names ?? {},
  marketIso: video?.marketIso ?? '',
  gameVersion: video?.gameVersion ?? '',
  gameVersionReleaseDate: video?.gameVersionReleaseDate ?? 0,
  thumbnails: video?.thumbnails ?? [],
  chapters: video?.chapters ?? [],
})

/** Legacy's update: the whole loaded video with the edits. Legacy also sent client-only `name` and chapter `title`, which the backend drops. */
export const toFtueVideoUpdateBody = (video: FtueVideo, values: FtueVideoFormValues): FtueVideoUpdateBody => ({
  ...video,
  gameVersion: values.gameVersion,
  gameVersionReleaseDate: values.gameVersionReleaseDate,
  marketIso: values.marketIso,
  thumbnails: values.thumbnails,
  names: values.names,
  chapters: toChapterBodies(values.chapters),
})

export type FtueVideoField = 'gameId' | 'vimeoId' | 'names' | 'marketIso' | 'gameVersion' | 'thumbnails' | 'chapters'

export const ftueVideoFieldLabelKeys: { [field in FtueVideoField]: string } = {
  gameId: 'internal-analysts:ftue_videos_game',
  vimeoId: 'internal-analysts:ftue_videos_vimeo_id',
  names: 'internal-analysts:ftue_videos_name',
  marketIso: 'internal-analysts:ftue_videos_market',
  gameVersion: 'internal-analysts:ftue_videos_version',
  thumbnails: 'internal-analysts:ftue_videos_thumbnail',
  chapters: 'internal-common:ftue_chapters',
}

/** The sent values the stored video doesn't have, so a 2xx that didn't store everything is reported. */
export const getUnstoredFtueVideoFields = (sent: FtueVideoAttachBody | FtueVideoUpdateBody, stored?: FtueVideo | null): FtueVideoField[] => {
  if (!stored) return ['gameId', 'vimeoId', 'names', 'marketIso', 'gameVersion', 'thumbnails', 'chapters']
  const unstored: FtueVideoField[] = []
  if (sent.gameId !== stored.gameId) unstored.push('gameId')
  if (sent.vimeoId !== stored.vimeoId) unstored.push('vimeoId')
  if (!translationsMatch(sent.names, stored.names)) unstored.push('names')
  if (sent.marketIso !== stored.marketIso) unstored.push('marketIso')
  if (sent.gameVersion !== stored.gameVersion || sent.gameVersionReleaseDate !== stored.gameVersionReleaseDate) unstored.push('gameVersion')
  if (!thumbnailsMatch(sent.thumbnails, stored.thumbnails)) unstored.push('thumbnails')
  if (!chaptersMatch(sent.chapters, stored.chapters ?? [])) unstored.push('chapters')
  return unstored
}
